import {
  ArrowRight, ShieldCheck, Boxes, Library, Radar, LayoutDashboard, Users, Lock,
  Printer, PhoneCall, ThumbsUp, Rocket, ChevronRight, Quote,
} from "lucide-react";
import { useSectionTracking } from "@/lib/useSectionTracking";
import { FlagstarEngagement } from "@/pages/flagstar/FlagstarEngagement";
import { FlagstarDataPrivacy } from "@/pages/flagstar/DataPrivacy";
import { PlatformScreens } from "@/components/PlatformScreens";

/* ============================================================================
 * Flagstar Bank — Enterprise Third-Party Risk Management proposal.
 * Rendered for slug `flagstar-bank` (code UCMD2M). Mirrors the printed
 * one-pager: $8,000 setup (20% off with term sheet by month-end) · $600 onboarding per vendor ·
 * proposed $1,200/qtr review per critical vendor (optional, 50–200 slider) · $1,000/mo platform ·
 * $200/hr blended fractional add-on · 45-day commitment.
 * ========================================================================== */

const TEAL = "#00586B";
const GOLD = "#FFC425";
const CHARCOAL = "#1A1A1A";
const OFFWHITE = "#F7F7F5";

const PILLARS = [
  {
    Icon: Radar,
    t: "OFAC / PEP / negative news — onboarding and ongoing",
    b: "Every vendor is screened against OFAC, PEP, and adverse media at onboarding — then enrolled in continuous monitoring. A new list match or negative-news hit triggers an alert the same day, not at the next annual review.",
  },
  {
    Icon: LayoutDashboard,
    t: "One scorecard for leadership",
    b: "Tiers, screening status, review currency, open findings, and remediation aging — rolled up across the whole vendor book into a single view leadership and examiners can actually read.",
  },
  {
    Icon: Library,
    t: "A prompt library your team authors",
    b: "Your team authors and controls the prompts that drive every review — so training new analysts is faster and consistency is built in. The review of vendor #4 is held to exactly the same standard as vendor #400, and an examiner can trace how.",
  },
  {
    Icon: Lock,
    t: "Privacy by design — a closed AI environment",
    b: "Leverage AI without uploading client data — or any data — into Claude's systems. Inference runs in a closed AWS Bedrock environment inside your own cloud: data never leaves your VPC, is encrypted with your keys, is never used for model training, and nothing is retained after the request.",
    link: "privacy",
    linkLabel: "See how we protect your data →",
  },
  {
    Icon: Users,
    t: "Fractional experts on call",
    b: "When a review escalates or an exam is coming, pull in CompliSun practitioners at $200/hr blended — as-needed, no monthly minimum. Real compliance people, not a ticket queue.",
  },
  {
    Icon: ShieldCheck,
    t: "Built by practitioners who've sat in your seat",
    b: "Attorney-led, staffed by former bank CCOs and BSA officers. We build the program the way we'd want to defend it in an exam — because we've had to.",
  },
];

const PATH = [
  { Icon: PhoneCall, n: "1", t: "Discovery call", b: "Free — we learn your systems, vendor book, and headaches." },
  { Icon: Boxes, n: "2", t: "Prototype", b: "No cost, no commitment — configured in ~2 weeks." },
  { Icon: ThumbsUp, n: "3", t: "You decide", b: "Go live, or walk away — no obligation." },
  { Icon: Rocket, n: "4", t: "Go live", b: "Live enterprise-wide within 45 days." },
];

const TRACKED_SECTIONS = ["hero", "platform", "privacy", "inside", "path", "pricing"];

export default function FlagstarProposal() {
  const { seenSections, engagementPct } = useSectionTracking(TRACKED_SECTIONS);
  const handlePrint = () => window.print();
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div data-testid="flagstar-proposal" className="min-h-screen w-full antialiased" style={{ background: OFFWHITE, color: CHARCOAL }}>
      <style>{`
        .fs-h { font-family: 'Source Serif Pro', 'Source Serif 4', Georgia, serif; font-weight: 600; letter-spacing: -0.02em; line-height: 1.08; color: ${CHARCOAL}; }
        .fs-eyebrow { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: ${TEAL}; font-weight: 700; }
        .fs-card { background: #fff; border: 1px solid rgba(26,26,26,0.10); border-radius: 14px; transition: border-color .2s ease, box-shadow .2s ease; }
        .fs-card:hover { border-color: ${TEAL}55; box-shadow: 0 10px 30px -18px rgba(200,16,46,0.25); }
        .fs-anchor { scroll-margin-top: 90px; }
        @media print {
          [data-print="hide"], header { display: none !important; }
          .fs-card { box-shadow: none !important; break-inside: avoid; }
          section { break-inside: avoid-page; padding-top: 20px !important; padding-bottom: 20px !important; }
        }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(247,247,245,0.9)", borderBottom: "1px solid rgba(26,26,26,0.08)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <span className="fs-h text-[15px]">CompliSun</span>
            <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] hidden sm:inline-block ml-1 px-2 py-1 rounded-full"
                  style={{ background: `${TEAL}12`, color: TEAL }}>For Flagstar</span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {[["Platform", "platform"], ["Data privacy", "privacy"], ["Inside", "inside"], ["How it works", "path"], ["Pricing", "pricing"]].map(([label, id]) => (
              <button key={id} onClick={() => jump(id)} data-testid={`fs-nav-${id}`}
                      className="text-[13px] font-bold px-3 py-2 rounded-md transition-colors hover:bg-black/5">
                {label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} data-testid="fs-nav-pdf"
                    className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[11px] font-bold uppercase tracking-[0.1em] border"
                    style={{ borderColor: `${CHARCOAL}25` }}>
              <Printer className="h-3.5 w-3.5" /> PDF
            </button>
            <button onClick={() => jump("pricing")} data-testid="fs-nav-cta"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-bold transition-transform hover:-translate-y-0.5"
                    style={{ background: GOLD, color: CHARCOAL }}>
              Design your engagement <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="fs-anchor relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-32 h-[480px] w-[480px] rounded-full blur-3xl opacity-[0.10]" style={{ background: TEAL }} />
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-14 md:pb-20 relative">
          <div className="flex flex-col items-start gap-4 mb-7">
            <img src="/brand/prospects/flagstar.png" alt="Flagstar Bank" className="h-10 md:h-12 object-contain" data-testid="flagstar-logo" />
            <span className="fs-eyebrow">Enterprise third-party risk management · prepared by CompliSun for Flagstar Bank</span>
          </div>
          <h1 className="fs-h text-5xl sm:text-6xl lg:text-7xl max-w-5xl" data-testid="fs-hero-headline">
            Standardize every review. Monitor every vendor. <span style={{ color: TEAL }}>Give leadership complete visibility.</span>
          </h1>
          <p className="mt-6 text-xl leading-relaxed max-w-3xl" style={{ opacity: 0.75 }}>
            <strong>One system, one standard, every vendor.</strong> CompliSun gives every critical vendor
            its own environment, holds every review to the same standard with a vetted prompt library, and
            screens continuously against OFAC/PEP/negative news from onboarding forward — rolled into a
            single scorecard your leadership and examiners can both read. Practitioners first. Technology
            as the force multiplier.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3" data-print="hide">
            <button onClick={() => jump("pricing")} data-testid="fs-hero-cta"
                    className="inline-flex items-center gap-2 h-14 px-7 rounded-md text-base font-bold transition-transform hover:-translate-y-0.5"
                    style={{ background: GOLD, color: CHARCOAL }}>
              Design your engagement <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => jump("inside")} className="inline-flex items-center gap-2 h-14 px-7 rounded-md text-base font-bold border-2" style={{ borderColor: `${CHARCOAL}20` }}>
              See inside the platform <ChevronRight className="h-4 w-4" />
            </button>
            <button onClick={handlePrint} data-testid="fs-hero-pdf"
                    className="inline-flex items-center gap-2 h-14 px-6 rounded-md text-base font-bold" style={{ color: `${CHARCOAL}99` }}>
              <Printer className="h-4 w-4" /> Download PDF
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-2">
            {["$1,000/mo flat platform fee", "45-day go-live commitment", "Privacy-by-design closed AI environment", "LendAPI Partner of the Year 2025"].map((c) => (
              <span key={c} className="text-[12px] font-bold px-3.5 py-1.5 rounded-full border" style={{ borderColor: `${TEAL}35`, color: TEAL, background: `${TEAL}08` }}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM PILLARS */}
      <section id="platform" className="fs-anchor py-14 md:py-20" style={{ background: "#fff", borderTop: "1px solid rgba(26,26,26,0.07)", borderBottom: "1px solid rgba(26,26,26,0.07)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="fs-eyebrow">The platform · what makes it different</div>
          <h2 className="fs-h text-4xl md:text-5xl mt-3 max-w-3xl">
            One review standard. Every vendor. <span style={{ color: TEAL }}>Always being watched.</span>
          </h2>
          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map(({ Icon, t, b, link, linkLabel }, i) => (
              <div key={t} className="fs-card p-6" data-testid={`fs-pillar-${i}`}>
                <div className="h-10 w-10 rounded-lg grid place-items-center mb-4" style={{ background: `${TEAL}0f` }}>
                  <Icon className="h-5 w-5" style={{ color: TEAL }} strokeWidth={1.8} />
                </div>
                <div className="fs-h text-lg mb-2">{t}</div>
                <p className="text-[14px] leading-relaxed" style={{ opacity: 0.72 }}>{b}</p>
                {link && (
                  <button onClick={() => jump(link)} data-testid={`fs-pillar-link-${link}`}
                          className="mt-3 text-[13px] font-bold transition-colors hover:opacity-80" style={{ color: TEAL }}>
                    {linkLabel}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="fs-card p-6 md:p-8" style={{ borderLeft: `4px solid ${TEAL}` }} data-testid="fs-testimonial">
              <Quote className="h-6 w-6 mb-3" style={{ color: TEAL }} />
              <p className="fs-h text-xl md:text-2xl leading-snug" style={{ fontWeight: 400 }}>
                "You need the right partner. We chose the right partner with CompliSun — the work gets done,
                no sacrifices, and far better quality than most full-time hires."
              </p>
              <p className="mt-3 text-[13px] font-bold" style={{ opacity: 0.65 }}>— Pia Thompson · CEO, Checkbook</p>
            </div>
            <div className="fs-card p-6 md:p-8" style={{ borderLeft: `4px solid ${TEAL}` }} data-testid="fs-testimonial-2">
              <Quote className="h-6 w-6 mb-3" style={{ color: TEAL }} />
              <p className="fs-h text-xl md:text-2xl leading-snug" style={{ fontWeight: 400 }}>
                "Partnering with CompliSun Guardian was instrumental in strengthening our compliance management
                systems. It significantly reduced both cost and time when implementing marketing audits and
                securing our lending licenses."
              </p>
              <p className="mt-3 text-[13px] font-bold" style={{ opacity: 0.65 }}>— Mark Mughabghab · CEO, RNPL Credit</p>
            </div>
          </div>
        </div>
      </section>

      {/* DATA PRIVACY — how we protect your data */}
      <FlagstarDataPrivacy />

      {/* INSIDE THE PLATFORM — prototype preview */}
      <PlatformScreens
        id="inside"
        accent={TEAL}
        lightbox
        eyebrow="Inside the platform · prototype preview"
        headingText="The prototype you sign off on."
        headingAccentText="Then we build it into your enterprise solution."
        intro="These screens are mock-ups of the prototype we configure for Flagstar. You approve the prototype, and we build it out into your full enterprise environment — the same path our clients took to get live. We're in production with clients on this platform today."
        footnote="Prototype mock-ups with sample data — the live build is configured to Flagstar's actual workflows."
      />

      {/* PATH + 45-DAY */}
      <section id="path" className="fs-anchor py-14 md:py-20" style={{ background: "#fff", borderTop: "1px solid rgba(26,26,26,0.07)", borderBottom: "1px solid rgba(26,26,26,0.07)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="fs-eyebrow">How to work with us</div>
          <h2 className="fs-h text-4xl md:text-5xl mt-3 max-w-3xl">
            No cost to see if this is a fit — <span style={{ color: TEAL }}>you only pay once you go live.</span>
          </h2>
          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PATH.map(({ Icon, n, t, b }) => (
              <div key={n} className="fs-card p-6 relative" data-testid={`fs-path-${n}`}>
                <div className="absolute top-4 right-5 fs-h text-4xl" style={{ color: `${TEAL}18` }}>{n}</div>
                <Icon className="h-5 w-5 mb-3" style={{ color: TEAL }} strokeWidth={1.8} />
                <div className="fs-h text-base mb-1.5">{t}</div>
                <p className="text-[13px] leading-relaxed" style={{ opacity: 0.72 }}>{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 fs-card p-6 md:p-7" style={{ borderColor: `${TEAL}40`, background: `${TEAL}05` }} data-testid="fs-commitment-45">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="fs-h text-5xl flex-shrink-0" style={{ color: TEAL }}>45<span className="text-xl align-top ml-1">days</span></div>
              <div>
                <div className="fs-h text-lg">The CompliSun Commitment</div>
                <p className="text-[14px] leading-relaxed mt-1" style={{ opacity: 0.78 }}>
                  We commit to a fully configured, tested, and <strong>live</strong> CompliSun program — built around
                  Flagstar's actual workflows — within 45 days of kickoff. If we miss that date for reasons within
                  our control, <strong>your first 2 months of platform fees are on us.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING / ENGAGEMENT BUILDER */}
      <FlagstarEngagement
        engagementPct={engagementPct}
        sectionsViewed={Array.from(seenSections)}
        onPrint={handlePrint}
      />

      {/* Footer */}
      <footer className="py-12" style={{ background: CHARCOAL, color: "#fff" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-bold">CompliSun, Inc.</div>
              <div className="text-[11px] opacity-60">Practitioners first. Technology as the force multiplier.</div>
            </div>
          </div>
          <div className="text-[12px] opacity-75">
            Colin Darke · Founder &amp; CEO · <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> · Cell (313) 617-4473
          </div>
        </div>
      </footer>
    </div>
  );
}
